import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  deleteMessageContactForm,
  markMessageContactForm,
  getAllMessagesContactForm,
} from "../../../../services/contactformServices";
import { useContactForm } from "../../../../contexts/ContactFormContext";
import {
  FaEnvelope,
  FaEnvelopeOpenText,
  FaSearch,
  FaSyncAlt,
  FaTrashAlt,
  FaCheckCircle,
  FaClock,
  FaInbox,
  FaUser,
} from "react-icons/fa";
import { toast } from "react-toastify";

/* ---------------- helpers ---------------- */

const CARD =
  "rounded-[26px] border border-slate-200/80 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 shadow-[0_18px_50px_-20px_rgba(15,23,42,0.28)] backdrop-blur";
const INPUT =
  "w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm text-slate-800 dark:text-slate-100 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/40";
const BUTTON_SOFT =
  "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-medium transition";

const formatDate = (d) =>
  new Date(d).toLocaleString("fr-FR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const formatShortDate = (d) =>
  new Date(d).toLocaleDateString("fr-FR", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });

const isUnread = (m) =>
  m &&
  (m.read_message === false || m.read_message === 0 || m.read_message === "0");

const excerpt = (text = "", max = 120) => {
  const value = String(text || "").trim();
  if (value.length <= max) return value;
  return `${value.slice(0, max)}…`;
};

const isToday = (dateStr) => {
  const d = new Date(dateStr);
  const now = new Date();
  return (
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()
  );
};

const isThisWeek = (dateStr) => {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  return diff <= 7 * 24 * 60 * 60 * 1000;
};

/* ---------------- UI bits ---------------- */

function StatCard({ icon, label, value, hint }) {
  return (
    <div className="rounded-[22px] border border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            {label}
          </div>
          <div className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-50">
            {value}
          </div>
          {hint ? (
            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {hint}
            </div>
          ) : null}
        </div>

        <div className="rounded-2xl bg-white dark:bg-slate-900 p-3 text-slate-700 dark:text-slate-200 shadow-sm">
          {icon}
        </div>
      </div>
    </div>
  );
}

function Badge({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    green:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
    orange:
      "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
    purple:
      "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300",
    red: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  };

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
}

function SectionHead({ eyebrow, title, description, action }) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        {eyebrow ? (
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-500">
            {eyebrow}
          </div>
        ) : null}
        <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-50">
          {title}
        </h2>
        {description ? (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {description}
          </p>
        ) : null}
      </div>
      {action ? <div>{action}</div> : null}
    </div>
  );
}

function ActionButton({
  icon,
  label,
  onClick,
  tone = "gray",
  disabled = false,
}) {
  const tones = {
    gray: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-900/40 dark:bg-blue-500/10 dark:text-blue-300 dark:hover:bg-blue-500/20",
    green:
      "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-900/40 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/20",
    red: "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-900/40 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed ${tones[tone] || tones.gray}`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function EmptyState() {
  return (
    <div className="py-16 grid place-items-center">
      <div className="text-center max-w-sm">
        <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 dark:bg-slate-800 grid place-items-center mb-4 text-xl">
          📨
        </div>
        <div className="font-semibold text-slate-900 dark:text-slate-50">
          Aucun message pour l’instant
        </div>
        <div className="text-sm text-slate-500 mt-1">
          Les nouveaux messages apparaîtront ici après rafraîchissement.
        </div>
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-20 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse"
        />
      ))}
    </div>
  );
}

/* ---------------- component ---------------- */

export default function ContactFormDisplay({ userId }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [sort, setSort] = useState("desc");
  const [selectedId, setSelectedId] = useState(null);

  const { unreadCountContactForm, setUnreadCountContactForm } =
    useContactForm();

  const refreshUnreadCounter = useCallback(
    (list) => {
      const count = (list || []).reduce(
        (acc, m) => acc + (isUnread(m) ? 1 : 0),
        0
      );
      setUnreadCountContactForm(count);
    },
    [setUnreadCountContactForm]
  );

  const loadMessages = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const data = await getAllMessagesContactForm(userId);
      const arr = Array.isArray(data) ? data : [];
      setMessages(arr);
      refreshUnreadCounter(arr);
    } catch (error) {
      console.error("Erreur lors du chargement des messages:", error);
      toast.error("Erreur lors du chargement des messages.");
    } finally {
      setLoading(false);
    }
  }, [userId, refreshUnreadCounter]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let arr = [...messages];

    if (q) {
      arr = arr.filter((m) => {
        const txt = `${m.name || ""} ${m.email || ""} ${m.message || ""}`;
        return txt.toLowerCase().includes(q);
      });
    }

    if (onlyUnread) {
      arr = arr.filter((m) => isUnread(m));
    }

    arr.sort((a, b) =>
      sort === "asc"
        ? new Date(a.created_at) - new Date(b.created_at)
        : new Date(b.created_at) - new Date(a.created_at)
    );

    return arr;
  }, [messages, query, onlyUnread, sort]);

  useEffect(() => {
    if (!filtered.length) {
      setSelectedId(null);
      return;
    }

    const exists = filtered.some((m) => m.id === selectedId);
    if (!exists) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  const selectedMessage =
    filtered.find((m) => m.id === selectedId) || filtered[0] || null;

  const stats = useMemo(() => {
    const unread = messages.filter((m) => isUnread(m)).length;
    const today = messages.filter((m) => isToday(m.created_at)).length;
    const week = messages.filter((m) => isThisWeek(m.created_at)).length;

    return {
      total: messages.length,
      unread,
      today,
      week,
    };
  }, [messages]);

  const handleDeleteMessage = async (messageId) => {
    const msg = messages.find((m) => m.id === messageId);
    const confirmDelete = window.confirm(
      "Supprimer définitivement ce message ?"
    );
    if (!confirmDelete) return;

    try {
      await deleteMessageContactForm(messageId);

      const next = messages.filter((m) => m.id !== messageId);
      setMessages(next);

      if (isUnread(msg)) {
        setUnreadCountContactForm((c) => Math.max(0, (c || 0) - 1));
      }

      toast.success("Message supprimé.");
    } catch (error) {
      console.error("Erreur lors de la suppression du message:", error);
      toast.error("Erreur lors de la suppression du message.");
    }
  };

  const toggleReadStatus = async (messageId) => {
    try {
      const message = messages.find((m) => m.id === messageId);
      if (!message) return;

      await markMessageContactForm(messageId);

      const next = messages.map((m) =>
        m.id === messageId ? { ...m, read_message: !m.read_message } : m
      );
      setMessages(next);

      setUnreadCountContactForm((prev) =>
        isUnread(message) ? Math.max(0, (prev || 0) - 1) : (prev || 0) + 1
      );

      toast.success(
        isUnread(message)
          ? "Message marqué comme lu."
          : "Message marqué comme non lu."
      );
    } catch (error) {
      console.error(
        "Erreur lors du basculement de l'état de lecture du message:",
        error
      );
      toast.error("Erreur lors de la mise à jour du message.");
    }
  };

  return (
    <div className="space-y-6">
      {/* KPI */}
      <section className={`${CARD} p-5 md:p-6`}>
        <SectionHead
          eyebrow="Vue d’ensemble"
          title="Boîte de réception"
          description="Suivez rapidement le volume de messages, les non lus et l’activité récente."
          action={
            <button
              type="button"
              onClick={loadMessages}
              className={`${BUTTON_SOFT} bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700`}
            >
              <FaSyncAlt />
              Rafraîchir
            </button>
          }
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard
            icon={<FaInbox />}
            label="Messages"
            value={stats.total}
            hint="Total chargé"
          />
          <StatCard
            icon={<FaEnvelope />}
            label="Non lus"
            value={unreadCountContactForm}
            hint="À traiter en priorité"
          />
          <StatCard
            icon={<FaCheckCircle />}
            label="Aujourd’hui"
            value={stats.today}
            hint="Messages du jour"
          />
          <StatCard
            icon={<FaClock />}
            label="7 jours"
            value={stats.week}
            hint="Activité récente"
          />
        </div>
      </section>

      {/* Filters */}
      <section className={`${CARD} p-5 md:p-6`}>
        <SectionHead
          eyebrow="Filtres"
          title="Trouver rapidement un message"
          description="Recherche par nom, e-mail ou contenu, avec filtre non lus."
        />

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
          <div className="relative xl:col-span-6">
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher (nom, e-mail, contenu)…"
              className={`${INPUT} pl-11`}
            />
          </div>

          <div className="xl:col-span-3 flex items-center gap-3 rounded-2xl border border-slate-200 dark:border-slate-700 px-4 py-3">
            <input
              id="onlyUnread"
              type="checkbox"
              checked={onlyUnread}
              onChange={(e) => setOnlyUnread(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <label
              htmlFor="onlyUnread"
              className="text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              Non lus uniquement
            </label>
          </div>

          <div className="xl:col-span-3">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className={INPUT}
              title="Trier par date"
            >
              <option value="desc">Plus récents d’abord</option>
              <option value="asc">Plus anciens d’abord</option>
            </select>
          </div>
        </div>
      </section>

      {/* Main content */}
      <section className={`${CARD} overflow-hidden`}>
        <div className="border-b border-slate-200 dark:border-slate-800 px-5 py-4 md:px-6">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                Messages reçus
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Sélectionnez un message à gauche pour afficher son contenu
                complet.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="purple">{filtered.length} affiché(s)</Badge>
              {unreadCountContactForm > 0 ? (
                <Badge tone="blue">
                  {unreadCountContactForm} non lu
                  {unreadCountContactForm > 1 ? "s" : ""}
                </Badge>
              ) : (
                <Badge tone="green">Tout est lu</Badge>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <SkeletonList />
        ) : filtered.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-0">
            {/* List */}
            <div className="xl:col-span-5 border-b xl:border-b-0 xl:border-r border-slate-200 dark:border-slate-800">
              <div className="max-h-[720px] overflow-y-auto p-4 space-y-3">
                {filtered.map((m) => {
                  const unread = isUnread(m);
                  const selected = selectedMessage?.id === m.id;

                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setSelectedId(m.id)}
                      className={`w-full rounded-[24px] border p-4 text-left transition ${
                        selected
                          ? "border-indigo-300 bg-indigo-50/80 dark:border-indigo-700 dark:bg-indigo-500/10"
                          : unread
                          ? "border-blue-200 bg-blue-50/70 dark:border-blue-900/40 dark:bg-blue-500/10"
                          : "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-semibold text-slate-900 dark:text-slate-50">
                              {m.name || "—"}
                            </div>
                            {unread ? (
                              <Badge tone="blue">Non lu</Badge>
                            ) : (
                              <Badge tone="green">Lu</Badge>
                            )}
                            {isToday(m.created_at) ? (
                              <Badge tone="orange">Aujourd’hui</Badge>
                            ) : null}
                          </div>

                          <div className="mt-1 text-sm text-blue-600 dark:text-blue-400 break-all">
                            {m.email || "—"}
                          </div>

                          <div className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-6">
                            {excerpt(m.message, 110)}
                          </div>
                        </div>

                        <div className="shrink-0 text-xs text-slate-500 dark:text-slate-400">
                          {formatShortDate(m.created_at)}
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        <ActionButton
                          icon={unread ? <FaEnvelopeOpenText /> : <FaEnvelope />}
                          label={unread ? "Marquer lu" : "Marquer non lu"}
                          tone={unread ? "blue" : "gray"}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleReadStatus(m.id);
                          }}
                        />
                        <ActionButton
                          icon={<FaTrashAlt />}
                          label="Supprimer"
                          tone="red"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteMessage(m.id);
                          }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Detail */}
            <div className="xl:col-span-7 p-4 md:p-6">
              {selectedMessage ? (
                <div className="xl:sticky xl:top-4 rounded-[28px] border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/30 p-5 md:p-6">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50">
                          {selectedMessage.name || "Message sans nom"}
                        </h3>

                        {isUnread(selectedMessage) ? (
                          <Badge tone="blue">Non lu</Badge>
                        ) : (
                          <Badge tone="green">Lu</Badge>
                        )}
                      </div>

                      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                        <span className="inline-flex items-center gap-2">
                          <FaEnvelope />
                          <a
                            href={`mailto:${selectedMessage.email || ""}`}
                            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 break-all"
                          >
                            {selectedMessage.email || "—"}
                          </a>
                        </span>

                        <span className="hidden sm:inline">•</span>

                        <span className="inline-flex items-center gap-2">
                          <FaClock />
                          {formatDate(selectedMessage.created_at)}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <ActionButton
                        icon={
                          isUnread(selectedMessage) ? (
                            <FaEnvelopeOpenText />
                          ) : (
                            <FaEnvelope />
                          )
                        }
                        label={
                          isUnread(selectedMessage)
                            ? "Marquer comme lu"
                            : "Marquer comme non lu"
                        }
                        tone={isUnread(selectedMessage) ? "blue" : "gray"}
                        onClick={() => toggleReadStatus(selectedMessage.id)}
                      />
                      <ActionButton
                        icon={<FaTrashAlt />}
                        label="Supprimer"
                        tone="red"
                        onClick={() => handleDeleteMessage(selectedMessage.id)}
                      />
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="rounded-2xl bg-white dark:bg-slate-900 p-4 border border-slate-200 dark:border-slate-800">
                      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        Expéditeur
                      </div>
                      <div className="mt-2 flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">
                          <FaUser />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 dark:text-slate-50">
                            {selectedMessage.name || "—"}
                          </div>
                          <div className="text-sm text-slate-500 dark:text-slate-400 break-all">
                            {selectedMessage.email || "—"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl bg-white dark:bg-slate-900 p-4 border border-slate-200 dark:border-slate-800">
                      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        Réception
                      </div>
                      <div className="mt-2 font-semibold text-slate-900 dark:text-slate-50">
                        {formatDate(selectedMessage.created_at)}
                      </div>
                      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        {isToday(selectedMessage.created_at)
                          ? "Message reçu aujourd’hui"
                          : "Historique conservé"}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 rounded-2xl bg-white dark:bg-slate-900 p-5 border border-slate-200 dark:border-slate-800">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      Message
                    </div>
                    <div className="mt-3 whitespace-pre-wrap leading-7 text-slate-700 dark:text-slate-200">
                      {selectedMessage.message || "Aucun contenu."}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid min-h-[320px] place-items-center text-center">
                  <div>
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">
                      <FaInbox />
                    </div>
                    <div className="font-semibold text-slate-900 dark:text-slate-50">
                      Sélectionnez un message
                    </div>
                    <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Le contenu complet s’affichera ici.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}